import React from 'react';
import {
  AddMoreButton,
  FormFItemWrapper,
  FormFLinkText,
  PaymentSchemeDetailTableHeader
} from '../../styled';
import { Box, Typography } from '@mui/material';
import pallet from '../../../../layouts/pallet';
import Icons from '../../../../components/icons';
import paymentSchemesApis from '../../../../apis/payments/paymentSchemes';
import { FormFTabContentProps } from '../../types';
import { useRef, useState } from 'react';
import { useFeeStructureContext } from '../../context/FeeStructureContext';
import { isEmpty } from 'lodash';
import { SnackbarSeverityType } from '../../../../enums';

const FormFTabContent = ({
  schemeName,
  formFList,
  setFormFList,
  onDeleteFormFItem
}: FormFTabContentProps) => {
  const {
    selectedGrade,
    loggedInUser,
    selectedAcademicYear,
    admissionType,
    showToast
  } = useFeeStructureContext();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleAddMore = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);

    setUploading(true);
    try {
      const response = await paymentSchemesApis.uploadFormFAttachment({
        school: loggedInUser.school,
        academicYear: selectedAcademicYear,
        grade: selectedGrade,
        files,
        schemeName,
        isNewAdmissionPaymentSchemeAttachments:
          admissionType === 'newAdmission',
        attachmentType: 'FORM_F_ATTACHMENTS',
        addTimeStampInFileName: !isEmpty(formFList) || files.length > 1
      });

      const attachments = response as any;

      const uploadedAttachments = attachments.uploadedAttachments;

      const newlyAddedFormFs = uploadedAttachments?.map((attachment: any) => ({
        filename: attachment.ev_filename || attachment.filename,
        url: attachment.ev_url || attachment.url,
        mimetype: attachment.ev_mimetype || attachment.mimetype,
        size: attachment.ev_size || attachment.size
      }));

      setFormFList((prev) => [...prev, ...(newlyAddedFormFs ?? [])]);
      const uploadedCount = uploadedAttachments?.length ?? 0;
      showToast(
        `${uploadedCount} PDF${
          uploadedCount > 1 ? 's have' : ' has'
        } been uploaded successfully.`,
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Failed to upload PDFs.',
        SnackbarSeverityType.error
      );
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: pallet.yellowBackground,
          padding: '12px 16px',
          display: 'flex',
          gap: '12px',
          borderRadius: '6px',
          marginBottom: '16px'
        }}
      >
        <Icons.AlertYellowFilled />
        <Box>
          <Typography color={pallet.yellowAlertText}>** Note</Typography>
          <Typography sx={{ marginTop: '4px' }} color={pallet.yellowAlertText}>
            Please upload a PDF file (Maximum file size: 5 MB).
          </Typography>
        </Box>
      </Box>
      <PaymentSchemeDetailTableHeader>
        <Typography fontWeight='700' color={pallet.formLabelText}>
          File Name
        </Typography>
      </PaymentSchemeDetailTableHeader>
      {formFList.map((item, idx) => (
        <FormFItemWrapper key={item.uploadId || item.url || idx}>
          <a
            href={item.url}
            target='_blank'
            rel='noopener noreferrer'
            style={FormFLinkText}
          >
            {item.filename || 'Form-F.pdf'}
          </a>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Icons.Delete
              style={{ cursor: 'pointer' }}
              onClick={() => onDeleteFormFItem(idx)}
            />
          </Box>
        </FormFItemWrapper>
      ))}
      <input
        type='file'
        accept='application/pdf'
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={uploading}
        multiple
      />
      <AddMoreButton
        onClick={handleAddMore}
        disabled={uploading}
        sx={{ marginTop: isEmpty(formFList) ? '16px' : '0' }}
      >
        <Icons.PlusPurpleFilled style={{ marginRight: '8px' }} />{' '}
        {uploading ? 'Uploading...' : 'Upload Form'}
      </AddMoreButton>
    </>
  );
};

export default FormFTabContent;
